import React from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import type { ModalChildrenProps } from '@noths/polaris-client-ribbons-design-system';
import {
  HyperButtonLink,
  IconCheckmark,
  Modal,
  OutlineButton,
  SolidButtonLink,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { getProductImageURL, priceFormatter } from '@noths/polaris-client-utils';

import { PriceBreakdownContainer } from 'src/components/molecules/PriceBreakdown/PriceBreakdownContainer';
import { BASKET_PAGE_PATH } from 'src/constants/page';
import type { BasketState } from 'src/redux/basket/slice';
import type { ProductState } from 'src/redux/product/types';
import {
  BASKET_MODAL_BUTTON_CONTINUE,
  BASKET_MODAL_BUTTON_CTA,
  BASKET_MODAL_CONTENT_LABEL,
  BASKET_MODAL_HEADER,
  BASKET_MODAL_HEADER_EDIT,
  BASKET_MODAL_QUANTITY,
  BASKET_MODAL_TOTAL,
} from '../copy';
import { useMembership } from '../useMembership';
import { MembershipActive } from './MembershipActive';
import { MembershipUpsell } from './MembershipUpsell';
import { SmallBusinessLove } from './SmallBusinessLove';
import * as styles from './BasketModal.styles';

export interface BasketModalStateProps {
  basket: BasketState['basket'];
  editMode: boolean;
  open: boolean;
  product: ProductState;
  quantity: number;
  showMembershipUpsell: boolean;
}

export interface BasketModalDispatchProps {
  onClose: VoidFunction;
  onNavigateToBasket: VoidFunction;
}

export type BasketModalProps = BasketModalDispatchProps & BasketModalStateProps;

const BasketSummary = ({ basket }: Pick<BasketModalProps, 'basket'>) => {
  const hasDiscount =
    basket?.itemsTotal && basket?.discountedItemsTotal
      ? basket.itemsTotal.cents > basket.discountedItemsTotal?.cents
      : false;
  const discountedTotalFormatted = basket?.discountedItemsTotal
    ? priceFormatter(basket.discountedItemsTotal.currency, basket.discountedItemsTotal.cents)
    : null;
  const itemsTotalFormatted = basket?.itemsTotal
    ? priceFormatter(basket.itemsTotal.currency, basket.itemsTotal.cents)
    : null;
  const itemsCount = basket?.itemsCount || 0;

  return (
    <div css={styles.summary}>
      {discountedTotalFormatted && (
        <p css={styles.summaryTotal}>
          {`${BASKET_MODAL_TOTAL}: ${discountedTotalFormatted}`}
          {hasDiscount && <s>{itemsTotalFormatted}</s>}
        </p>
      )}
      {itemsCount > 0 && (
        <p css={styles.summaryItems}>
          {`${basket!.itemsCount} ${itemsCount === 1 ? 'item' : 'items'}`}
          <span css={visuallyHidden}> in the basket</span>
        </p>
      )}
    </div>
  );
};

const CTAButtons = ({
  onClose,
  onNavigateToBasket,
}: Pick<BasketModalProps, 'onNavigateToBasket' | 'onClose'>) => {
  return (
    <div css={styles.buttonContainer}>
      <OutlineButton fullWidth onClick={onClose}>
        {BASKET_MODAL_BUTTON_CONTINUE}
      </OutlineButton>
      <SolidButtonLink
        css={styles.goToBasketButton}
        fullWidth
        href={BASKET_PAGE_PATH}
        onClick={(e) => {
          e.preventDefault();
          onNavigateToBasket();
        }}
        role="button"
      >
        {BASKET_MODAL_BUTTON_CTA}
      </SolidButtonLink>
    </div>
  );
};

export const BasketModal = ({
  basket,
  editMode,
  onClose,
  onNavigateToBasket,
  open,
  product: { images, partner, title },
  quantity,
  showMembershipUpsell,
}: BasketModalProps) => {
  const isOnSmallScreen = useBreakpoint({ initializeWithLarge: true }) === Breakpoint.S;
  const { membershipIsActive } = useMembership();
  const membershipIsInBasket =
    basket?.membershipAddedStatus && basket?.membershipAddedStatus !== 'NOT_ADDED';
  const enableAddMembership = !membershipIsActive && !membershipIsInBasket;

  return (
    <>
      <Modal
        appRootSelector="#NOTHSPageWrapper"
        contentLabel={BASKET_MODAL_CONTENT_LABEL}
        isModalOpen={open}
        onModalClose={onClose}
      >
        {({ Body, Footer, Header }: ModalChildrenProps) => (
          <>
            <Header notificationStatus="success" onCloseButtonClick={onClose}>
              <div css={styles.headerSection}>
                <IconCheckmark height={24} width={24} />
                <h2 css={styles.header}>
                  {editMode ? BASKET_MODAL_HEADER_EDIT : BASKET_MODAL_HEADER}
                </h2>
              </div>
            </Header>
            <Body>
              <div>
                <div css={[styles.section, styles.productSection]}>
                  <div css={styles.productInfo}>
                    <img
                      alt=""
                      css={styles.productImage}
                      height={135}
                      src={getProductImageURL(images[0].href, 'mini')}
                      width={135}
                    />
                    <div>
                      <HyperButtonLink href={partner.links?.[1]?.rel || ''}>
                        {partner.name}
                      </HyperButtonLink>
                      <p css={styles.productDescription}>{title}</p>
                      <p css={styles.productQuantity}>{`${BASKET_MODAL_QUANTITY}: ${quantity}`}</p>
                    </div>
                  </div>
                  <div css={styles.breakdownWrapper}>
                    <PriceBreakdownContainer expandable totalDivider />
                  </div>
                </div>
                <div css={[styles.section, styles.buttonSection]}>
                  {!isOnSmallScreen && (
                    <>
                      <BasketSummary basket={basket} />
                      {showMembershipUpsell ? (
                        enableAddMembership ? (
                          <MembershipUpsell />
                        ) : (
                          <MembershipActive />
                        )
                      ) : (
                        <SmallBusinessLove />
                      )}
                      <CTAButtons onClose={onClose} onNavigateToBasket={onNavigateToBasket} />
                    </>
                  )}
                </div>
              </div>
            </Body>
            {isOnSmallScreen && (
              <Footer>
                <div css={styles.footerItems}>
                  <BasketSummary basket={basket} />
                  {showMembershipUpsell ? (
                    enableAddMembership ? (
                      <MembershipUpsell />
                    ) : (
                      <MembershipActive />
                    )
                  ) : (
                    <SmallBusinessLove />
                  )}
                  <CTAButtons onClose={onClose} onNavigateToBasket={onNavigateToBasket} />
                </div>
              </Footer>
            )}
          </>
        )}
      </Modal>
      <styles.BasketModalGlobalStyles />
    </>
  );
};
